/**
 * Currency Configuration - Hisab OK Hai v1
 *
 * Hard-set to INR (Indian Rupee) for v1.
 * No multi-currency, FX rates, or currency conversion in this phase.
 *
 * Future: Multi-currency will be a Pro feature, ledger-wise,
 * based on user feedback and international rollout.
 *
 * ═══════════════════════════════════════════════════════════════════
 * ENTERPRISE UI RULES - Amount & Color Conventions
 * ═══════════════════════════════════════════════════════════════════
 *
 * COLOR USAGE:
 * - Green (#27AE60):   Income / positive balance / success states
 * - Red (#E74C3C):     Expense transactions / errors / warnings ONLY
 * - Purple (#6366F1):  Dues / payables / receivables (neutral)
 * - Blue (#4A90E2):    Balance / info / primary actions
 * - Dark (#2c3e50):    Neutral text / default amount display
 *
 * NEGATIVE VALUE RULES:
 * - Use red ONLY for explicit expense transactions
 * - Use neutral purple for dues/payables (not red, not negative sign)
 * - Negative balances should use dark text (#2c3e50), NOT red
 *
 * CSS CLASSES:
 * - .text-success:  Green (#27AE60) - income
 * - .text-danger:   Red (#E74C3C) - expense ONLY
 * - .text-neutral:  Purple (#6366F1) - dues/payables
 * ═══════════════════════════════════════════════════════════════════
 */

import { FaRupeeSign } from 'react-icons/fa';
import { FiDollarSign } from 'react-icons/fi';

const CURRENCY = {
  code: 'INR',
  symbol: '₹',
  locale: 'en-IN',
  name: 'Indian Rupee',
};

/**
 * Currency-aware icon component.
 * Maps the configured currency code to the correct icon.
 * Use <CurrencyIcon /> anywhere you need a currency icon in the UI.
 */
const CURRENCY_ICON_MAP = {
  INR: FaRupeeSign,
  USD: FiDollarSign,
};

export const CurrencyIcon = CURRENCY_ICON_MAP[CURRENCY.code] || FiDollarSign;

/**
 * Format an amount for display with the INR symbol.
 * @param {number|string} amount - The raw numeric amount
 * @param {number} decimals - Decimal places (default 2)
 * @returns {string} Formatted string e.g. "₹1,234.56"
 */
export const formatAmount = (amount, decimals = 2) => {
  const num = parseFloat(amount) || 0;
  return `${CURRENCY.symbol}${num.toLocaleString(CURRENCY.locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}`;
};

/**
 * Format a signed amount (+ for income, - for expense).
 * @param {number|string} amount
 * @param {'income'|'expense'} type
 * @param {number} decimals
 * @returns {string} e.g. "+₹500.00" or "-₹200.00"
 */
export const formatSignedAmount = (amount, type, decimals = 2) => {
  const sign = type === 'income' ? '+' : '-';
  const num = parseFloat(amount) || 0;
  return `${sign}${CURRENCY.symbol}${num.toLocaleString(CURRENCY.locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}`;
};

export default CURRENCY;
